import React, { useEffect, useRef, useState } from 'react';

const STATES = new Set(['pending', 'processing', 'completed', 'blocked', 'failed', 'expired']);
const PROOF_CODES = new Set(['BEFID_IDENTITY_PROOF_REQUIRED', 'BEFID_FRESH_PROOF_REQUIRED', 'IDENTITY_VERIFICATION_REQUIRED', 'BEFID_PHONE_PROOF_REQUIRED']);
const POLL_INTERVAL = 3000;
const MAX_POLLS = 100;

// The recovery capability and phone number are deliberately never persisted.
function PhoneChange({ token, expectedLinkId, identityBase, requestHeaders = {}, onReverify, onClose, onStatusChange }) {
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [stage, setStage] = useState('request');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [uncertain, setUncertain] = useState(false);
  const active = useRef(false);
  const capability = useRef(null);
  const controllerRef = useRef(null);
  const timer = useRef(null);
  const polls = useRef(0);
  const invalidated = useRef(false);
  const heading = useRef(null);
  const headersKey = JSON.stringify(requestHeaders);

  const invalidateIdentity = () => {
    if (!invalidated.current) {
      invalidated.current = true;
      onStatusChange?.(null);
    }
  };

  const perform = async (action) => {
    if (!active.current || controllerRef.current) return;
    if (action === 'request' && (!consent || !/^\+?[\d ()-]{10,22}$/.test(phone.trim()))) return;
    if (action === 'confirm' && (!confirmed || !/^\d{6}$/.test(code) || !capability.current)) return;
    if (action === 'status' && !capability.current) return;
    clearTimeout(timer.current);
    const controller = new AbortController();
    controllerRef.current = controller;
    setBusy(true);
    setError(null);
    let nextStage;
    try {
      const body = action === 'request' ? { expectedLinkId, newPhone: phone.trim(), confirmation: true }
        : action === 'confirm' ? { ...capability.current, code, confirmation: true, expectedLinkId }
          : capability.current;
      const response = await fetch(`${identityBase}/phone-change/${action}`, {
        method: 'POST', credentials: 'include', cache: 'no-store', signal: controller.signal,
        headers: { ...Object.fromEntries(Object.entries(requestHeaders).filter(([name]) => name.toLowerCase() !== 'authorization')), 'Content-Type': 'application/json', ...(action === 'status' ? {} : { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => null);
      if (!active.current || controller.signal.aborted) return;
      if (!response.ok) {
        if (action === 'status' && response.status === 410) {
          setStage('followup_expired');
          setCode('');
          setConfirmed(false);
          setUncertain(false);
          invalidateIdentity();
          return;
        }
        const reverify = action !== 'status' && ((action === 'request' && response.status === 401) || PROOF_CODES.has(payload?.code));
        setError({ reverify, message: reverify ? 'Vérifiez de nouveau votre ancien numéro de téléphone avant de poursuivre.'
          : response.status === 429 ? 'Trop de tentatives. Patientez avant de réessayer.'
            : action === 'confirm' ? 'Le code n’a pas pu être validé. Vérifiez-le, puis consultez l’état de la demande avant de réessayer.'
              : action === 'status' ? 'Impossible de consulter la demande pour le moment. Réessayez la vérification de son état.'
                : 'La demande n’a pas pu être créée. Vérifiez votre nouveau numéro puis réessayez.' });
        if (action === 'confirm') {
          setUncertain(true);
          if (response.status >= 500) invalidateIdentity();
        }
        return;
      }
      if (action === 'request') {
        if (typeof payload?.requestId !== 'string' || !payload.requestId || typeof payload.browserSecret !== 'string' || !payload.browserSecret
          || !Number.isFinite(Date.parse(payload.expiresAt))) throw new Error('INVALID_REQUEST');
        capability.current = { requestId: payload.requestId, browserSecret: payload.browserSecret };
        setPhone('');
        setConsent(false);
        setStage('pending');
      } else {
        if (!STATES.has(payload?.status)) throw new Error('INVALID_STATUS');
        nextStage = payload.status;
        setStage(nextStage);
        setUncertain(false);
        if (nextStage !== 'pending') {
          invalidateIdentity();
          setCode('');
          setConfirmed(false);
        }
      }
    } catch (_) {
      if (!active.current || controller.signal.aborted) return;
      if (action === 'confirm') { invalidateIdentity(); setUncertain(true); }
      setError({ message: action === 'request'
        ? 'La réponse d’envoi est indisponible. Aucun renvoi automatique ne sera effectué. Patientez avant de créer une nouvelle demande.'
        : 'La réponse est indisponible. Consultez l’état de cette demande ; aucun nouveau SMS ne sera envoyé.' });
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      if (active.current && !controller.signal.aborted) {
        setBusy(false);
        if (nextStage === 'processing' && polls.current < MAX_POLLS) {
          polls.current += 1;
          timer.current = setTimeout(() => perform('status'), POLL_INTERVAL);
        }
      }
    }
  };

  useEffect(() => {
    active.current = true;
    heading.current?.focus();
    return () => {
      active.current = false;
      clearTimeout(timer.current);
      controllerRef.current?.abort();
      controllerRef.current = null;
      capability.current = null;
    };
  }, [token, expectedLinkId, identityBase, headersKey]);

  return <div className="befid-phone-change" aria-busy={busy}>
    <h4 ref={heading} tabIndex={-1}>Changer mon numéro de téléphone</h4>
    <p>Votre ancien numéro doit avoir été vérifié récemment. Un code SMS sera envoyé au nouveau numéro pour vérifier qu’il vous appartient. Si vous n’avez plus accès à l’ancien numéro, contactez l’assistance.</p>
    <p>Le changement actualise votre identité BEFID et vos fiches dans tous les restaurants déjà liés. Vos points, cadeaux, codes promo, historiques et consentements sont conservés.</p>
    {error && <div role="alert"><p>{error.message}</p>{error.reverify && <button type="button" disabled={busy} onClick={onReverify}>Vérifier de nouveau mon ancien numéro</button>}</div>}
    {stage === 'request' && <form onSubmit={event => { event.preventDefault(); perform('request'); }}>
      <label>Nouveau numéro de téléphone<input type="tel" autoComplete="tel" inputMode="tel" value={phone} disabled={busy} onChange={event => setPhone(event.target.value)} maxLength={22} required /></label>
      <label className="befid-customer-identity-consent"><input type="checkbox" checked={consent} disabled={busy} onChange={event => setConsent(event.target.checked)} /><span>Je souhaite remplacer mon numéro dans mon identité BEFID et dans tous mes restaurants liés.</span></label>
      <button type="submit" disabled={busy || !consent || !/^\+?[\d ()-]{10,22}$/.test(phone.trim())}>{busy ? 'Demande en cours…' : 'Recevoir le code sur mon nouveau numéro'}</button>
    </form>}
    {stage === 'pending' && <form onSubmit={event => { event.preventDefault(); perform('confirm'); }}>
      <p>Saisissez les six chiffres du code reçu sur votre nouveau numéro.</p>
      <label>Code SMS du nouveau numéro<input type="text" inputMode="numeric" autoComplete="one-time-code" value={code} maxLength={6} disabled={busy || uncertain} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} /></label>
      <label className="befid-customer-identity-consent"><input type="checkbox" checked={confirmed} disabled={busy || uncertain} onChange={event => setConfirmed(event.target.checked)} /><span>Je confirme le changement de numéro dans tous mes restaurants liés. Mes sessions BEFID seront fermées et je devrai ensuite me reconnecter.</span></label>
      <button type="submit" disabled={busy || uncertain || !confirmed || !/^\d{6}$/.test(code)}>{busy ? 'Vérification…' : 'Confirmer mon nouveau numéro'}</button>
    </form>}
    {stage === 'processing' && <p role="status">Changement en cours… Vous pouvez fermer cette fenêtre : votre demande continue d’être traitée. Vous pourrez ensuite vous reconnecter avec votre nouveau numéro.</p>}
    {stage === 'completed' && <div role="status"><p>Votre numéro a été changé dans votre identité BEFID et vos restaurants liés. Vos avantages et historiques sont conservés.</p><button type="button" onClick={onReverify}>Me reconnecter avec mon nouveau numéro</button></div>}
    {stage === 'blocked' && <p role="alert">Le changement a été interrompu. Une partie des établissements peut déjà être actualisée. Contactez l’assistance pour vérifier son état ; ne démarrez pas une autre demande.</p>}
    {stage === 'failed' && <p role="alert">Cette demande n’a pas abouti. Aucun changement de numéro n’a été effectué par cette demande. Vérifiez de nouveau votre téléphone avant de recommencer.</p>}
    {stage === 'followup_expired' && <div role="alert"><p>Le suivi a expiré. Votre demande peut encore être en cours de traitement. Reconnectez-vous avec votre nouveau numéro ou contactez l’assistance pour vérifier son état.</p><button type="button" onClick={onReverify}>Me reconnecter avec mon nouveau numéro</button></div>}
    {stage === 'expired' && <p role="alert">Cette demande a expiré. Vérifiez de nouveau votre téléphone avant de reprendre le parcours.</p>}
    {capability.current && !['completed', 'blocked', 'failed', 'expired', 'followup_expired'].includes(stage) && <button className="befid-identity-secondary" type="button" disabled={busy} onClick={() => { polls.current = 0; perform('status'); }}>Vérifier l’état de ma demande</button>}
    {['expired', 'failed'].includes(stage) && <button type="button" onClick={onReverify}>Vérifier de nouveau mon téléphone</button>}
    <button className="befid-identity-secondary" type="button" disabled={busy} onClick={onClose}>Fermer</button>
  </div>;
}

export default function BefidCustomerPhoneChange(props) {
  return <PhoneChange key={`${props.token}\u0000${props.expectedLinkId}\u0000${props.identityBase}\u0000${JSON.stringify(props.requestHeaders || {})}`} {...props} />;
}
